import { findStrategy } from "../../component/Methods";

/** SMA fund ids from managers (same as `Object.keys(managers[0]?.smas || {})`). */
export function getSmaFundIds(managers) {
  return Object.keys(managers?.[0]?.smas || {});
}

export function excludeSmaFromFunds(funds, managers) {
  const smaIds = getSmaFundIds(managers);
  return funds?.filter((f) => !smaIds.includes(f?.fundId)) || [];
}

export function excludeSmaFromLots(lots, managers) {
  const smaIds = getSmaFundIds(managers);
  return lots?.filter((lot) => !smaIds.includes(lot?.fund)) || [];
}

/** @param accountingFunds `accountingData[0]?.funds` */
export function excludeSmaFromAccountingFunds(accountingFunds, managers) {
  const smaIds = getSmaFundIds(managers);
  const rows = accountingFunds || [];
  return rows.filter((row) => !smaIds.includes(row?.fund));
}


export function getPositionStableRowId(position) {
  const cust = position.custodian ?? "no-custodian";
  const sym = position.tickerSymbol ?? "";
  const strat = position.strategy ?? "no-strategy";
  return `${position.fund}_${cust}_${sym}_${strat}`;
}

export function sortPositionsList(positions, sortField, sortDirection) {
  return [...positions].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    if (sortField === "tickerSymbol") {
      const aStartsWithNumber = /^\d/.test((aValue || "").toLowerCase());
      const bStartsWithNumber = /^\d/.test((bValue || "").toLowerCase());

      if (aStartsWithNumber && !bStartsWithNumber) return 1;
      if (!aStartsWithNumber && bStartsWithNumber) return -1;

      aValue = (aValue || "").toLowerCase();
      bValue = (bValue || "").toLowerCase();
    } else if (
      sortField === "shares" ||
      sortField === "averageCostPerShare" ||
      sortField === "price"
    ) {
      aValue = parseFloat(aValue) || 0;
      bValue = parseFloat(bValue) || 0;
    } else {
      aValue = (aValue || "").toLowerCase();
      bValue = (bValue || "").toLowerCase();
    }

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });
}

export function buildDisplayedPositions({
  originalPositions,
  selectedFund,
  selectedStrategy,
  search,
  sortField,
  sortDirection,
}) {
  let filtered = originalPositions || [];

  if (selectedFund) {
    filtered = filtered.filter((item) => item.fund === selectedFund);
  }

  if (selectedStrategy) {
    filtered = filtered.filter((item) => item.strategy === selectedStrategy);
  }

  if (search) {
    const searchLower = search.toLowerCase();
    filtered = filtered.filter(
      (item) =>
        (item.tickerSymbol || "").toLowerCase().includes(searchLower) ||
        (item.fundName || "").toLowerCase().includes(searchLower) ||
        (item.strategyName || "").toLowerCase().includes(searchLower) ||
        (item.custodianName || "").toLowerCase().includes(searchLower)
    );
  }

  const sortedFiltered = sortPositionsList(filtered, sortField, sortDirection);

  return sortedFiltered.map((position) => ({
    ...position,
    id: getPositionStableRowId(position),
  }));
}

export function getStrategiesWithPositiveAum({
  accountingFunds,
  selectedFundId,
  strategies,
}) {
  if (!selectedFundId || !Array.isArray(accountingFunds) || accountingFunds.length === 0) {
    return [];
  }
  const accFund = accountingFunds.find((row) => row?.fund === selectedFundId);
  const aums = accFund?.strategyAUMs;
  if (!aums || typeof aums !== "object") return [];

  return Object.entries(aums)
    .filter(([, aum]) => Number(aum) > 0)
    .map(([strategyId]) => ({
      strategyId,
      name: findStrategy(strategyId, strategies) || strategyId,
    }))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
}

/** Fund ids that appear on at least one position row. */
export function getFundIdsWithPositions(positions) {
  const ids = new Set();
  (positions || []).forEach((p) => {
    if (p?.fund) ids.add(p.fund);
  });
  return ids;
}

export function filterFundsWithPositions(funds, positions) {
  if (!Array.isArray(funds)) return [];
  const withPos = getFundIdsWithPositions(positions);
  return funds.filter((f) => f?.fundId && withPos.has(f.fundId));
}

/**
 * Strategies with accounting AUM &gt; 0 for the fund, intersected with strategies
 * that actually have position rows for that fund.
 */
export function getStrategiesWithPositiveAumAndPositions({
  accountingFunds,
  selectedFundId,
  strategies,
  positions,
}) {
  const positive = getStrategiesWithPositiveAum({
    accountingFunds,
    selectedFundId,
    strategies,
  });
  if (!selectedFundId || !Array.isArray(positions)) {
    return positive;
  }

  const strategyIdsWithPositions = new Set(
    positions
      .filter((p) => p?.fund === selectedFundId && p?.strategy)
      .map((p) => p.strategy)
  );
  return positive.filter((s) => strategyIdsWithPositions.has(s.strategyId));
}

/** WebSocket action for fund+strategy close (preview uses submit: false). */
export const CLOSE_STRATEGY_ACTION = "close_strategy";
export function buildCloseStrategyWebSocketPayload(fundId, strategyId, submit = false) {
  return {
    action: CLOSE_STRATEGY_ACTION,
    data: {
      fundId,
      strategyId,
      submit,
    },
  };
}
